import { Button, Icon, IconProps, StandardButtonProps } from '@platform/components';
import { AriaButtonOptions } from 'react-aria';
import { formatDateStr } from '../../dashboard/my-projects/utils';
import React from 'react';

type CTACardProps = {
  title: string;
  description: string | React.ReactNode;
  ctaButtonProps: StandardButtonProps & AriaButtonOptions<'button'>;
  titleIcon?: IconProps['type'];
  subText?: string;
};

export const CTACard = ({
  title,
  description,
  ctaButtonProps,
  titleIcon,
  subText,
}: CTACardProps) => {
  return (
    <div className='border border-gray-100 bg-secondary-brand-25 rounded-xl p-4 flex flex-col gap-2 text-center md:text-left'>
      <div className='flex gap-2'>
        {titleIcon && <Icon type={titleIcon} stroke='brand-500' />}
        <h3 className='text-md font-semibold'>{title}</h3>
      </div>
      <div className='text-md mb-2'>{description}</div>
      <Button {...ctaButtonProps} additionalClassnames='w-fit mx-auto md:mx-0'>
        {ctaButtonProps.children}
      </Button>
      {subText && <p className='text-xs text-gray-500'>{subText}</p>}
    </div>
  );
};

/** Similar to CTA card but contains icon and content indicating the user has already taken action. CTA is still available */
export const UserSubmittedCTACard = ({
  onPress,
  isDisabled,
  latestUpdateDate,
}: {
  onPress: () => void;
  isDisabled?: boolean;
  latestUpdateDate?: string;
}) => {
  return (
    <CTACard
      titleIcon='check-circle'
      title='Update received'
      description="We've saved your last update. You can submit another update if anything has changed, if you still need support, or if you've completed this step so we can move you forward."
      ctaButtonProps={{
        variant: 'primary',
        size: 'sm',
        children: 'Add update',
        onPress,
        isDisabled,
      }}
      subText={latestUpdateDate ? `Last received: ${formatDateStr(latestUpdateDate)}` : undefined}
    />
  );
};

export const CompletedCTACard = ({ completedDate }: { completedDate: string }) => {
  return (
    <CTACard
      titleIcon='check-circle'
      title='Step completed'
      description="Thanks for the update – this step is now complete. You don't need to take any further action here."
      ctaButtonProps={{
        variant: 'primary',
        size: 'sm',
        children: 'Update received',
        isDisabled: true,
      }}
      subText={`Last received: ${formatDateStr(completedDate)}`}
    />
  );
};
